import './MobileViewport.scss';

import {LatLng, Poi, Trip} from '../../react-app-env';
import React, {useEffect} from 'react';
import Sidebar from '../sidebar/Sidebar';
import MapView from '../map/mapview/MapView';
import metaDataService from '../../services/MetaDataService';
import Icon, {IconSizes} from '../icon/Icon';
import {Events, EventSubscription, EventType} from '../../events/Events';
import {delay} from '../../utils/Timing';

export interface MobileViewportProps {
  trip: Trip;
  googleMapsApiKey: string;
}

enum ViewMode {
  TRIP,
  MAP,
  SPLIT
}

/**
 * Waits 250ms until Map component has its final viewport size, then re-centers the location.
 *
 * @param {LatLng} location The location to center in the MapView.
 */
function triggerDelayedCenterInMap(location: LatLng) {
  delay(250, () => {
    Events.publish(EventType.CENTER_IN_MAP, location);
  });
}

/**
 * Fernrausch main viewport for mobile devices.
 */
const MobileViewport: React.FC<MobileViewportProps> = ({ trip, googleMapsApiKey }) => {
  const [viewMode, setViewMode] = React.useState<ViewMode>(ViewMode.TRIP);
  const [selectedPoi, setSelectedPoi] = React.useState<Poi>();

  useEffect(() => {
    /**
     * Make sure the map is visible if a location is being centered in the MapView.
     */
    function watchCenterInMap(): EventSubscription {
      return Events.subscribe(EventType.CENTER_IN_MAP, (location) => {
        if (viewMode === ViewMode.TRIP) {
          setViewMode(ViewMode.SPLIT);
          triggerDelayedCenterInMap(location);
        }
      });
    }

    /**
     * Ensure the MapView isn't full-screen if a POI's detailed are being shown.
     */
    function watchPoiSelected(): EventSubscription {
      return Events.subscribe(EventType.SELECT_POI, (poi: Poi) => {
        setSelectedPoi(poi);
        if (viewMode === ViewMode.MAP) {
          setViewMode(ViewMode.SPLIT);
        }
      });
    }

    /**
     * Keep an eye on the currently selected POI.
     */
    function watchPoiUnselected(): EventSubscription {
      return Events.subscribe(EventType.ON_POI_UNSELECTED, () => {
        setSelectedPoi(undefined);
      });
    }

    const centerInMapSubscription = watchCenterInMap();
    const poiSelectedSubscription = watchPoiSelected();
    const poiUnselectedSubscription = watchPoiUnselected();

    // Clean up after ourselves
    return () => {
      centerInMapSubscription.unsubscribe();
      poiSelectedSubscription.unsubscribe();
      poiUnselectedSubscription.unsubscribe();
    };
  }, [viewMode, selectedPoi]);

  const viewModeClass = `fr-mobile-splitview__${ViewMode[viewMode].toLowerCase()}`;

  /**
   * Switches to the specified view mode.
   *
   * @param {ViewMode} targetViewMode View mode to switch to.
   */
  function switchToViewMode(targetViewMode: ViewMode) {
    setViewMode(targetViewMode);

    // If we're opening a map for the first time since selecting a POI, center the map on that POI.
    if (viewMode === ViewMode.TRIP && selectedPoi != null) {
      triggerDelayedCenterInMap(selectedPoi.location);
      setSelectedPoi(undefined);
    }
  }

  return (
      <main className="fr-mobile">
        <div className={'fr-mobile-splitview ' + viewModeClass}>
          <div className="fr-mobile-splitview-trip">
            <Sidebar trip={trip}/>
          </div>
          <div className="fr-mobile-splitview-map">
            <SplitViewButtons viewMode={viewMode}
                              tripTitle={trip.title}
                              onViewModeChange={(viewMode) => switchToViewMode(viewMode)}/>
            <MapView trip={trip}
                     googleMapsApiKey={googleMapsApiKey}
                     onGoogleMapsApiLoaded={(api) => {
                       metaDataService.setGoogleMapsApi(api);
                     }}/>
          </div>
        </div>
      </main>
  );
};

/**
 * Properties of SplitViewButtons.
 */
interface SplitViewButtonsProps {
  tripTitle: string;
  viewMode: ViewMode;
  onViewModeChange: (viewMode: ViewMode) => void;
}

/**
 * Buttons to control switching between map/trip/split view in mobile viewport.
 *
 * @param {ViewMode} viewMode Current view mode.
 * @param {string} tripTitle Title of the trip.
 * @param {(viewMode: ViewMode) => void} onViewModeChange Callback to change the view mode.
 */
const SplitViewButtons: React.FC<SplitViewButtonsProps> = ({ tripTitle, viewMode, onViewModeChange }) => {

  function renderButtonsLeft(): React.ReactNode {
    return <div className="fr-mobile-splitview-buttons-left">
      {(viewMode === ViewMode.MAP || viewMode === ViewMode.SPLIT) && (
          <button onClick={() => Events.publish(EventType.SHOW_TRIP_IN_MAP)}>
            <Icon category={'fa-expand'}
                  size={IconSizes.MEDIUM}/>
          </button>
      )}
    </div>;
  }

  function renderButtonsRight(): React.ReactNode {
    return <div className="fr-mobile-splitview-buttons-right">
      {viewMode === ViewMode.SPLIT && (
          <button onClick={() => onViewModeChange(ViewMode.MAP)}>
            <Icon category={'fa-angle-up'}
                  size={IconSizes.MEDIUM}/>
          </button>
      )}
      {viewMode === ViewMode.MAP && (
          <button onClick={() => onViewModeChange(ViewMode.SPLIT)}>
            <Icon category={'fa-angle-down'}
                  size={IconSizes.MEDIUM}/>
          </button>
      )}
      {(viewMode === ViewMode.MAP || viewMode === ViewMode.SPLIT) && (
          <button onClick={() => onViewModeChange(ViewMode.TRIP)}>
            <Icon category={'fa-close'}
                  size={IconSizes.MEDIUM}/>
          </button>
      )}
    </div>;
  }

  function renderButtonOpenMap(): React.ReactNode {
    if (viewMode === ViewMode.TRIP) {
      return <button className="map"
                     onClick={() => onViewModeChange(ViewMode.SPLIT)}>
        <Icon category={'fa-location-dot'}
              size={IconSizes.MEDIUM}/>
      </button>;
    } else {
      return null;
    }
  }

  function renderTripTitle(): React.ReactNode {
    return <div className="fr-mobile-splitview-buttons-center">
      {viewMode === ViewMode.MAP && (
          tripTitle
      )}
    </div>;
  }

  return <div className="fr-mobile-splitview-buttons">
    {renderButtonsLeft()}
    {renderTripTitle()}
    {renderButtonsRight()}
    {renderButtonOpenMap()}
  </div>;
};

export default MobileViewport;
